import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import { authApi } from '../api/services'
import { useAuth } from '../context/AuthContext'

const OTP_LENGTH = 6
const RESEND_WAIT = 60

const UserRegistration = () => {
  const navigate = useNavigate()
  const { login } = useAuth()

  const [step, setStep] = useState('form')

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  })

  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''))
  const [resendTimer, setResendTimer] = useState(0)
  const inputRefs = useRef([])

  const [isSendingOtp, setIsSendingOtp] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [isRegistering, setIsRegistering] = useState(false)

  // Countdown timer
  useEffect(() => {
    if (resendTimer <= 0) return
    const id = setTimeout(() => setResendTimer(t => t - 1), 1000)
    return () => clearTimeout(id)
  }, [resendTimer])

  const handleChange = e =>
    setFormData({ ...formData, [e.target.name]: e.target.value })

  // ── STEP 1: validate then send OTP ───────────────────────────────────────
  const handleSendOtp = async e => {
    e.preventDefault()
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters')
      return
    }
    setIsSendingOtp(true)
    try {
      // await authApi.sendOtp({ email: formData.email.trim().toLowerCase() });
      await authApi.sendOtp(formData.email.trim().toLowerCase())
      toast.success(`OTP sent to ${formData.email}`)
      setStep('otp')
      setResendTimer(RESEND_WAIT)
      setTimeout(() => inputRefs.current[0]?.focus(), 100)
    } catch (err) {
      toast.error(
        err.response?.data?.error || 'Failed to send OTP. Please try again.'
      )
    } finally {
      setIsSendingOtp(false)
    }
  }

  // ── OTP box handlers ──────────────────────────────────────────────────────
  const handleOtpChange = (i, value) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    const next = [...otpDigits]
    next[i] = digit
    setOtpDigits(next)
    if (digit && i < OTP_LENGTH - 1) inputRefs.current[i + 1]?.focus()
  }

  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      if (otpDigits[i]) {
        const next = [...otpDigits]
        next[i] = ''
        setOtpDigits(next)
      } else if (i > 0) {
        const next = [...otpDigits]
        next[i - 1] = ''
        setOtpDigits(next)
        inputRefs.current[i - 1]?.focus()
      }
    }
  }

  const handleOtpPaste = e => {
    e.preventDefault()
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH)
    if (!pasted) return
    const next = Array(OTP_LENGTH).fill('')
    pasted.split('').forEach((ch, idx) => {
      next[idx] = ch
    })
    setOtpDigits(next)
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus()
  }

  // ── STEP 2: verify OTP then register ─────────────────────────────────────
  const handleVerifyAndRegister = async e => {
    e.preventDefault()
    const otp = otpDigits.join('')
    if (otp.length < OTP_LENGTH) {
      toast.error(`Please enter all ${OTP_LENGTH} digits`)
      return
    }

    // 2a. Verify OTP
    setIsVerifyingOtp(true)
    try {
      await authApi.verifyOtp(formData.email.trim().toLowerCase(), otp)
      toast.success('Email verified!')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid or expired OTP.')
      setIsVerifyingOtp(false)
      return
    }
    setIsVerifyingOtp(false)

    // 2b. Register the account
    setIsRegistering(true)
    try {
      const res = await authApi.register({
        full_name: formData.fullName,
        email: formData.email.trim().toLowerCase(),
        password: formData.password
      })
      const userData = res.data?.data || res.data
      login(userData)
      // All new registrations are super_admin — send them to Org Setup
      navigate('/tenants', { replace: true })
    } catch (err) {
      toast.error(
        err.response?.data?.error || 'Registration failed. Please try again.'
      )
    } finally {
      setIsRegistering(false)
    }
  }

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendTimer > 0) return
    setIsSendingOtp(true)
    try {
      // await authApi.sendOtp({ email: formData.email.trim().toLowerCase() });
      await authApi.sendOtp(formData.email.trim().toLowerCase())
      toast.success('New OTP sent!')
      setOtpDigits(Array(OTP_LENGTH).fill(''))
      setResendTimer(RESEND_WAIT)
      setTimeout(() => inputRefs.current[0]?.focus(), 100)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend OTP.')
    } finally {
      setIsSendingOtp(false)
    }
  }

  const inputCls =
    'w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm ' +
    'focus:outline-none focus:border-blue-900 focus:ring-1 focus:ring-blue-900 transition-colors'

  return (
    <>
      <Toaster position='top-right' />
      <div className='h-full flex flex-col justify-center items-center px-10'>
        {/* ── STEP 1: Form ─────────────────────────────────────────────────── */}
        {step === 'form' && (
          <>
            <h1 className='text-3xl font-bold text-gray-800 text-center mb-2'>
              Create Account
            </h1>
            <p className='text-sm text-gray-500 text-center mb-6'>
              We'll send a verification code to your email.
            </p>

            <form
              onSubmit={handleSendOtp}
              className='w-full max-w-xs space-y-4'
            >
              <input
                type='text'
                name='fullName'
                placeholder='Full Name'
                value={formData.fullName}
                required
                onChange={handleChange}
                className={inputCls}
              />

              <input
                type='email'
                name='email'
                placeholder='Email'
                value={formData.email}
                required
                onChange={handleChange}
                className={inputCls}
              />

              <input
                type='password'
                name='password'
                placeholder='Password (min 8 chars)'
                value={formData.password}
                required
                onChange={handleChange}
                className={inputCls}
              />

              <input
                type='password'
                name='confirmPassword'
                placeholder='Confirm Password'
                value={formData.confirmPassword}
                required
                onChange={handleChange}
                className={inputCls}
              />

              <button
                type='submit'
                disabled={isSendingOtp}
                className='w-full py-3 bg-blue-900 text-white font-bold rounded-lg shadow-md
                           hover:bg-blue-800 active:scale-95 transition-all
                           disabled:opacity-60 disabled:cursor-not-allowed'
              >
                {isSendingOtp ? 'Sending OTP…' : 'Send Verification Code'}
              </button>
            </form>
          </>
        )}

        {/* ── STEP 2: OTP Entry ────────────────────────────────────────────── */}
        {step === 'otp' && (
          <>
            <button
              onClick={() => setStep('form')}
              className='self-start mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-blue-900 transition-colors'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
              Back
            </button>

            <div className='w-full max-w-xs text-center'>
              {/* Email icon */}
              <div className='w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4'>
                <svg
                  className='w-8 h-8 text-blue-900'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={1.5}
                    d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                  />
                </svg>
              </div>

              <h1 className='text-2xl font-bold text-gray-800 mb-1'>
                Verify your email
              </h1>
              <p className='text-sm text-gray-500 mb-1'>
                We sent a 6-digit code to
              </p>
              <p className='text-sm font-semibold text-blue-900 mb-6 break-all'>
                {formData.email}
              </p>

              <form onSubmit={handleVerifyAndRegister} className='space-y-6'>
                {/* OTP boxes */}
                <div
                  className='flex justify-center gap-2'
                  onPaste={handleOtpPaste}
                >
                  {otpDigits.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => (inputRefs.current[i] = el)}
                      type='text'
                      inputMode='numeric'
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      className={`w-11 h-12 text-center text-xl font-bold border-2 rounded-lg
                        focus:outline-none focus:ring-2 transition-all
                        ${
                          digit
                            ? 'border-blue-900 bg-blue-50 text-blue-900 focus:ring-blue-300'
                            : 'border-gray-200 bg-gray-50 text-gray-700 focus:ring-blue-200 focus:border-blue-400'
                        }`}
                    />
                  ))}
                </div>

                <button
                  type='submit'
                  disabled={
                    isVerifyingOtp ||
                    isRegistering ||
                    otpDigits.join('').length < OTP_LENGTH
                  }
                  className='w-full py-3 bg-blue-900 text-white font-bold rounded-lg shadow-md
                             hover:bg-blue-800 active:scale-95 transition-all
                             disabled:opacity-60 disabled:cursor-not-allowed'
                >
                  {isVerifyingOtp
                    ? 'Verifying…'
                    : isRegistering
                    ? 'Creating account…'
                    : 'Verify & Create Account'}
                </button>
              </form>

              {/* Resend */}
              <p className='mt-5 text-sm text-gray-500'>
                Didn't receive it?{' '}
                {resendTimer > 0 ? (
                  <span className='text-gray-400'>
                    Resend in{' '}
                    <span className='font-semibold text-blue-900'>
                      {resendTimer}s
                    </span>
                  </span>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={isSendingOtp}
                    className='text-blue-900 font-semibold hover:underline disabled:opacity-50'
                  >
                    {isSendingOtp ? 'Sending…' : 'Resend OTP'}
                  </button>
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default UserRegistration
